
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Book, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle, 
  Shield, Info, Lock, Key, Server
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface SecurityContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const SecurityContent: React.FC<SecurityContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "security-overview") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Security Overview</h1>
          <p className="text-gray-500 mt-2">
            Learn about the security measures we've implemented to protect your data and privacy.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 10, 2023</span>
            <Badge variant="outline" className="ml-2">Essential</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="security-approach">Our Security Approach</h2>
          <p>
            At DGK Platform, security is a core value. We implement a multi-layered security strategy that addresses 
            threats at every level of our technology stack, from infrastructure to application, ensuring your data and 
            transactions remain secure.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Infrastructure Security</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Enterprise-grade hosting with physical and network security measures.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Lock className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Application Security</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Secure coding practices, regular audits, and penetration testing.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Key className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Data Security</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Encryption at rest and in transit, secure data handling policies.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="key-security-features">Key Security Features</h2>
          
          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-base font-medium">
                End-to-End Encryption
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  All sensitive data is encrypted using industry-standard encryption algorithms both while stored on our servers 
                  and during transmission. This includes personal information, payment details, and project data.
                </p>
                <div className="bg-blue-50 p-3 rounded mt-2 text-sm">
                  <p className="text-blue-800">
                    <strong>Technical Details:</strong> We use AES-256 for data at rest and TLS 1.3 for data in transit.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-base font-medium">
                Multi-Factor Authentication (MFA)
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  Add an extra layer of security to your account with MFA. We support app-based authentication, SMS verification, 
                  and hardware security keys.
                </p>
                <div className="flex items-center mt-3">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" /> 
                  <span>Free for all account tiers</span>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-base font-medium">
                Secure Payment Processing
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  Our payment system is PCI DSS compliant and integrates with trusted payment processors. We never store your full credit 
                  card details on our servers.
                </p>
                <div className="flex items-center mt-3">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" /> 
                  <span>Escrow protection for all transactions</span>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger className="text-base font-medium">
                Regular Security Audits
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  We conduct regular internal and third-party security audits to identify and address potential vulnerabilities 
                  before they can be exploited.
                </p>
                <div className="flex items-center mt-3">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" /> 
                  <span>SOC 2 Type II certified</span>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 my-6">
            <h3 className="text-base font-medium text-yellow-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Security Best Practices
            </h3>
            <p className="text-yellow-700 text-sm mb-3">
              While we implement robust security measures, we recommend these practices for maximum account security:
            </p>
            <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1">
              <li>Enable Multi-Factor Authentication for your account</li>
              <li>Use a unique, strong password</li>
              <li>Regularly monitor your account activity</li>
              <li>Keep your contact information updated</li>
              <li>Be cautious of phishing attempts</li>
            </ul>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["security-overview"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("security-overview", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("security-overview", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("security-overview") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("security-overview")}
            >
              {bookmarkedArticles.includes("security-overview") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "data-privacy") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Data Privacy</h1>
          <p className="text-gray-500 mt-2">
            Understanding how we protect your personal data and privacy rights.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 15, 2023</span>
            <Badge variant="outline" className="ml-2">Essential</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="data-privacy-principles">Our Data Privacy Principles</h2>
          <p>
            DGK Platform is committed to protecting your privacy and ensuring transparent data practices. We adhere to these core principles:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600">
                  <Check className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Transparency</h3>
                <p className="text-sm text-gray-600">Clear information about how your data is collected, used, and shared.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600">
                  <Check className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Control</h3>
                <p className="text-sm text-gray-600">You maintain control over your personal data with tools to access and manage it.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600">
                  <Check className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Purpose Limitation</h3>
                <p className="text-sm text-gray-600">We only collect data for specific, legitimate purposes.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600">
                  <Check className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Data Minimization</h3>
                <p className="text-sm text-gray-600">We collect only what's necessary for our services to function properly.</p>
              </div>
            </div>
          </div>

          <h2 id="data-handling">How We Handle Your Data</h2>
          <p>
            We implement robust measures to ensure the safety and appropriate use of your personal data:
          </p>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-3">Data Storage and Security</h3>
            <div className="space-y-3 text-sm text-blue-700">
              <div className="flex items-start">
                <Server className="h-4 w-4 mt-1 mr-2 flex-shrink-0" />
                <p className="m-0">Data is stored in secure, certified data centers with physical and digital security measures.</p>
              </div>
              <div className="flex items-start">
                <Lock className="h-4 w-4 mt-1 mr-2 flex-shrink-0" />
                <p className="m-0">All personal data is encrypted both at rest and during transmission.</p>
              </div>
              <div className="flex items-start">
                <Shield className="h-4 w-4 mt-1 mr-2 flex-shrink-0" />
                <p className="m-0">Access to personal data is strictly controlled and monitored.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["data-privacy"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("data-privacy", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("data-privacy", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("data-privacy") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("data-privacy")}
            >
              {bookmarkedArticles.includes("data-privacy") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
